import type {
  CellClickedEventArgs,
  HeaderClickedEventArgs,
  Item,
} from "@glideapps/glide-data-grid";
import type {
  AiScriptChatSelectionContext,
  IAiSpreadsheetLoadedSheet,
} from "@/payroll/ai-script/types";
import type { AdaptedSpreadsheetSheet } from "./adapter";
import type { AiScriptSpreadsheetViewerProps } from "./ai-script-spreadsheet-viewer-types";
import { rectangleContainsCell } from "./cell-selection";
import { buildAiScriptChatCellQuestion } from "./chat-selection-context";
import { serializeTabSeparatedClipboard } from "./clipboard-text";
import type { createGridKeyboardHandlers } from "./grid-keyboard-handlers";
import { getSavedSpreadsheetRow } from "./spreadsheet-region-view";
import type { ViewerComments } from "./use-viewer-comments";
import type { ViewerUiState } from "./use-viewer-ui-state";
import {
  buildWorkbookCopiedRange,
  getGridCopyRange,
} from "./workbook-clipboard";

interface CreateGridPointerHandlersParams {
  canViewEditedCells: boolean;
  canNoteFromSelection: boolean;
  onAskAiAboutCell:
    | ((context: AiScriptChatSelectionContext) => void)
    | undefined;
  cellLink: AiScriptSpreadsheetViewerProps["cellLink"];
  cellSource?: AiScriptSpreadsheetViewerProps["cellSource"];
  adaptedSheet: AdaptedSpreadsheetSheet | null;
  activeLoadedSheet: IAiSpreadsheetLoadedSheet | null;
  regionRowMap: number[];
  maxDisplayRow: number;
  canMutateActiveSheet: boolean;
  canEditSheetLayout?: boolean;
  openSpreadsheetColumnMenu: ReturnType<
    typeof createGridKeyboardHandlers
  >["openSpreadsheetColumnMenu"];
  comments: Pick<
    ViewerComments,
    | "areCommentsEnabled"
    | "commentIndex"
    | "savedCellCoordinate"
    | "openCommentCard"
  >;
  ui: Pick<
    ViewerUiState,
    | "setColumnHeaderMenu"
    | "setRowMenu"
    | "lastContextPointerRef"
    | "handleMultiSelectCellClick"
    | "selectionBeforePointerDownRef"
  >;
}

export const createGridPointerHandlers = ({
  canViewEditedCells,
  canNoteFromSelection,
  onAskAiAboutCell,
  cellLink,
  cellSource,
  adaptedSheet,
  activeLoadedSheet,
  regionRowMap,
  maxDisplayRow,
  canMutateActiveSheet,
  canEditSheetLayout = false,
  openSpreadsheetColumnMenu,
  comments: {
    areCommentsEnabled,
    commentIndex,
    savedCellCoordinate,
    openCommentCard,
  },
  ui: {
    setColumnHeaderMenu,
    setRowMenu,
    lastContextPointerRef,
    handleMultiSelectCellClick,
    selectionBeforePointerDownRef,
  },
}: CreateGridPointerHandlersParams) => {
  const getCellContext = (col: number, row: number) => {
    if (!adaptedSheet || !activeLoadedSheet) return null;
    return buildAiScriptChatCellQuestion({
      adaptedSheet,
      activeSheet: activeLoadedSheet,
      cell: [col, row],
      getSavedRow: (displayRow) =>
        getSavedSpreadsheetRow(regionRowMap, displayRow),
    });
  };

  const getCellQuestionContext = (col: number, row: number) =>
    onAskAiAboutCell ? getCellContext(col, row) : null;

  const getCellLink = (col: number, row: number) => {
    if (!cellLink || row < 0) return null;
    const cell = getCellContext(col, row);
    return cell ? cellLink(cell.context) : null;
  };

  const getCellSource = (col: number, row: number) => {
    if (!cellSource || row < 0) return null;
    const cell = getCellContext(col, row);
    return cell ? cellSource(cell.context) : null;
  };

  // Only on a sheet the user cannot edit: activating a cell there starts an edit.
  const handleCellActivated = ([col, row]: Item) => {
    if (canMutateActiveSheet) return;
    getCellLink(col, row)?.onOpen();
  };

  const handleCellContextMenu = (cell: Item, event: CellClickedEventArgs) => {
    const gridRow = cell[1];
    if (
      gridRow < 0 ||
      gridRow >= maxDisplayRow ||
      (!canMutateActiveSheet &&
        !canNoteFromSelection &&
        !canEditSheetLayout &&
        !areCommentsEnabled &&
        !(
          canViewEditedCells &&
          adaptedSheet?.getCellRecord(cell[0], gridRow)?.override
        ) &&
        !getCellQuestionContext(cell[0], gridRow) &&
        !getCellLink(cell[0], gridRow) &&
        !getCellSource(cell[0], gridRow))
    ) {
      return;
    }
    event.preventDefault();
    setColumnHeaderMenu(null);
    let getNoteGridText: (() => string) | undefined;
    if (canNoteFromSelection && adaptedSheet) {
      const selectedRange = getGridCopyRange(
        selectionBeforePointerDownRef.current,
        adaptedSheet.rowCount,
        adaptedSheet.columns.length,
      );
      const range = {
        ...(selectedRange && rectangleContainsCell(selectedRange, cell)
          ? selectedRange
          : { x: cell[0], y: gridRow, width: 1, height: 1 }),
      };
      const sheetSnapshot = adaptedSheet;
      getNoteGridText = () => {
        const copiedRange = buildWorkbookCopiedRange({
          range,
          cells: sheetSnapshot.getCellsForSelection(range),
          getCellRecord: sheetSnapshot.getCellRecord,
        });
        return serializeTabSeparatedClipboard(copiedRange.values);
      };
    }
    setRowMenu({
      x: lastContextPointerRef.current.x,
      y: lastContextPointerRef.current.y,
      gridRow,
      col: cell[0],
      getNoteGridText,
    });
  };

  const handleCellClicked = (cell: Item, event: CellClickedEventArgs) => {
    if (handleMultiSelectCellClick(cell, event)) return;

    if (areCommentsEnabled && cell[1] >= 0) {
      const saved = savedCellCoordinate(cell[0], cell[1]);
      if (commentIndex.hasOpenThreadAt(saved.row, saved.col)) {
        openCommentCard(cell[0], cell[1], false);
      }
    }

    openSpreadsheetColumnMenu(cell, {
      x: lastContextPointerRef.current.x,
      y: lastContextPointerRef.current.y,
    });
  };

  const handleHeaderContextMenu = (
    col: number,
    event: HeaderClickedEventArgs,
  ) => {
    if (!canMutateActiveSheet) return;
    event.preventDefault();
    setRowMenu(null);
    setColumnHeaderMenu({
      x: lastContextPointerRef.current.x,
      y: lastContextPointerRef.current.y,
      columnIndex: col,
    });
  };

  return {
    canViewEditedCells,
    getCellQuestionContext,
    getCellLink,
    getCellSource,
    hasCellLinks: !!cellLink,
    handleCellActivated,
    handleCellContextMenu,
    handleCellClicked,
    handleHeaderContextMenu,
  };
};
